/**
 * {order}の一覧 /orders（#233、docs/93 §4.1）と {project}のタブ（GroupOrders）。左のメニュー「{order}」（現場力は{vendorQuote}の下・営業力は見積書の下）。
 *   タブ：承諾待ち／承諾済／下書き／すべて（件数つき）。承諾なしの会社（#253）は 発行済／下書き／すべて（件数のあるタブは足す＝契約の orderTabsShown）。
 *   探す（番号・{vendor}・件名）。列：No.・{vendor}・{project}・件名・金額（税抜）・発行日・承諾
 *   「{order}作成」は PC だけ（/orders/new。スマホは閲覧のみ）
 */
import { ORDER_MSG, orderTabsShown, type OrderList, type OrderSummary, type OrderTab } from "@genbaryoku/contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { usePageName } from "../../components/BackButton.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { mdSlash, yen } from "../../lib/num.js";
import { usePcOnly } from "../../lib/pcOnly.js";
import { OrderStateChip } from "./parts.jsx";

const TABS: [OrderTab, string][] = [
  ["waiting", ORDER_MSG.tabWaiting],
  ["accepted", ORDER_MSG.tabAccepted],
  ["issued", ORDER_MSG.tabIssued],
  ["draft", ORDER_MSG.tabDraft],
  ["all", ORDER_MSG.tabAll],
];

export function OrderListPage() {
  const L = useLabels();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.find(([k]) => k === sp.get("tab"))?.[0] ?? "all") as OrderTab;
  usePageName(L.order);
  return (
    <ListHead title={L.order}>
      <OrderTable
        tab={tab}
        onTab={(k) =>
          setSp((p) => {
            const n = new URLSearchParams(p);
            if (k === "all") n.delete("tab");
            else n.set("tab", k);
            return n;
          })
        }
        create={<CreateLink />}
      />
    </ListHead>
  );
}

/** {project}の画面のタブ「{order}」 */
export function GroupOrders({ groupId }: { groupId: string }) {
  const [tab, setTab] = useState<OrderTab>("all");
  return (
    <div data-testid="group-orders">
      <div className="row" style={{ justifyContent: "flex-end", marginBottom: 8 }}>
        <CreateLink group={groupId} />
      </div>
      <OrderTable tab={tab} onTab={setTab} group={groupId} />
    </div>
  );
}

function CreateLink({ group }: { group?: string }) {
  const pcOnly = usePcOnly();
  if (pcOnly.hidden("/orders/new")) return null;
  return (
    <Link className="btn primary" to={`/orders/new${group ? `?group=${group}` : ""}`} data-testid="order-new">
      {group ? null : <Icon k="plus" />}
      {t(ORDER_MSG.create)}
    </Link>
  );
}

/** 一覧（{order}の一覧＝create を渡すと一覧の頭の形＝表のカード・#241／{project}の画面のタブ） */
function OrderTable({ tab, onTab, group, create }: { tab: OrderTab; onTab: (k: OrderTab) => void; group?: string; create?: ReactNode }) {
  const L = useLabels();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  useEffect(() => {
    const h = window.setTimeout(() => setQApplied(q.trim()), 300);
    return () => window.clearTimeout(h);
  }, [q]);
  const list = useQuery({
    queryKey: ["orders", tab, qApplied, group ?? ""],
    queryFn: () => api<OrderList>("GET", `/orders?tab=${tab}${qApplied ? `&q=${encodeURIComponent(qApplied)}` : ""}${group ? `&group=${group}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const items: OrderSummary[] = list.data?.items ?? [];
  // 出すタブ（#253）：会社の承諾の既定で 承諾待ち・承諾済 か 発行済（読み込み中は承諾を使う形のまま＝前と同じ並び）
  const shown = list.data ? orderTabsShown(list.data.ackMode, list.data.counts) : (["waiting", "accepted", "draft", "all"] as OrderTab[]);
  const open = (o: OrderSummary) => nav(o.status === "draft" ? `/orders/${o.id}` : `/orders/${o.id}/paper`);
  const tid = group ? "gol" : "ol";
  const card = create !== undefined;
  return (
    <>
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <div className={card ? "lcard" : undefined}>
      <ListTabs
        id={tid}
        label={t("{order}を状態で絞る")}
        tabs={TABS.filter(([k]) => shown.includes(k) || k === tab).map(([k, label]) => ({ key: k, label, count: list.data?.counts[k] ?? "…" }))}
        value={tab}
        onChange={(k) => onTab(k as OrderTab)}
        filter={<FilterInput value={q} onChange={setQ} placeholder={t("番号・{vendor}・件名で絞り込み")} testId="order-search" />}
        create={create}
      />
      <div className="tbl-wrap ltab-panel" {...listTabPanel(tid, tab)}>
        <table className="list" data-testid="order-list">
          <thead>
            <tr>
              <th>{ORDER_MSG.colNo}</th>
              <th>{L.vendor}</th>
              {group ? null : <th>{L.project}</th>}
              <th>{ORDER_MSG.colSubject}</th>
              <th className="r">{ORDER_MSG.colAmount}</th>
              <th>{ORDER_MSG.colIssue}</th>
              <th>{ORDER_MSG.colAck}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="r" tabIndex={0} onClick={() => open(o)} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && open(o)} data-testid="order-row">
                <td className="num">{o.displayNo ?? o.documentNo ?? <span className="muted">未発行</span>}</td>
                <td><b>{o.vendorName}</b></td>
                {group ? null : <td>{o.group?.name ?? <span className="muted">—</span>}</td>}
                <td>{o.subject}</td>
                <td className="num r">{yen(o.subtotal)}</td>
                <td className="num">{o.status === "draft" ? <span className="muted">—</span> : mdSlash(o.issueDate)}</td>
                <td className="statecell"><OrderStateChip o={o} /></td>
              </tr>
            ))}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={group ? 6 : 7}>{t(ORDER_MSG.empty)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );
}
